// PrimeReact 9 ↔ 10 ObjectUtils compatibility shim.
//
// The wrapper library `@sl/react-datafabric-components` (v1.1.4) is built
// against `primereact@10.x` and calls a handful of `ObjectUtils.*` helpers
// that don't exist on our pinned `primereact@9.6.2`. The mismatch manifests
// as a hard runtime crash — most visibly:
//
//   TypeError: ht.getItemValue is not a function
//      at <minified bundle> in @sl/react-datafabric-components
//
// — which we hit on the candidate detail page because the Research tab
// renders the wrapper's <Tree>, which exercises the pt-resolution path
// that uses `getItemValue`.
//
// We polyfill the missing helpers on `ObjectUtils` at app boot so the
// wrapper finds what it expects. Each polyfill is a no-op if the method
// already exists, so when we eventually bump to primereact 10.x this file
// becomes inert and can be deleted.
//
// Import this file FIRST in `main.tsx` — before any wrapper component
// is touched. If you add another wrapper component and see another
// `ObjectUtils.foo is not a function`, add the corresponding shim here.

import { ObjectUtils } from 'primereact/utils';

const utils = ObjectUtils as unknown as Record<string, unknown>;

// `getItemValue(obj, ...params)` resolves the "value-or-callback" pattern
// that primereact 10's pt system relies on. If `obj` is a function, call
// it with the passed params; otherwise return `obj` as-is. Matches the
// behaviour of primereact 10's static method on ObjectUtils.
if (typeof utils.getItemValue !== 'function') {
  utils.getItemValue = function getItemValue(
    obj: unknown,
    ...params: unknown[]
  ): unknown {
    return typeof obj === 'function'
      ? (obj as (...a: unknown[]) => unknown)(...params)
      : obj;
  };
}

// `toFlatCase(s)` lowercases and strips hyphens — primereact 10's name
// for what 9.6.4 calls `convertToFlatCase`. Delegate to the 9.x impl
// when present so locale-aware edge cases match.
if (typeof utils.toFlatCase !== 'function') {
  utils.toFlatCase = function toFlatCase(value: unknown): string {
    const existing = utils.convertToFlatCase;
    if (typeof existing === 'function') {
      return (existing as (s: unknown) => string)(value);
    }
    return String(value ?? '')
      .toLowerCase()
      .replace(/-/g, '');
  };
}

// `isString(value)` — trivial type guard that primereact 10's pt
// resolution leans on.
if (typeof utils.isString !== 'function') {
  utils.isString = function isString(value: unknown): boolean {
    return typeof value === 'string';
  };
}
